import { Flex, Typography } from 'antd'
import { TransferKey } from 'antd/es/transfer/interface'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'
import { DarkButton, PrimaryButton } from '@/shared/ui/components/Button'
import {
  TRANSFER_MODES,
  TransferMode,
} from '@/pages/transfer/model/transferTypes'
import { useState } from 'react'
import { declineObjectWord } from '../DataTransferCard/lib/declineObjectWord'
import { useBodyScrollLock } from '@/app/hooks/useBodyScrollLock'
import { AttentionModal, LimitModal } from '../DataTransferCard/styled'
import { Icon } from '@/shared/ui/Icon/Icon'
import { ErrorAlert } from '@/shared/ui/components/Alert'

const { Text } = Typography

interface IProps {
  text: string
  loading?: boolean
  disabled?: boolean

  // Только сущности
  transferMode: TransferMode
  objectsTargetKeys: TransferKey[]
  usersTargetKeys: TransferKey[]
  geozonesTargetKeys: TransferKey[]
  driversTargetKeys: TransferKey[]
  retranslationsTargetKeys: TransferKey[]

  // Для режима "Полный перенос"
  accountsTargetKeys: TransferKey[]

  onClick?: () => void
}

export const TransferButtonSection = ({
  text,
  loading,
  disabled,

  transferMode,
  objectsTargetKeys,
  usersTargetKeys,
  geozonesTargetKeys,
  driversTargetKeys,
  retranslationsTargetKeys,

  // Для режима "Полный перенос"
  accountsTargetKeys,

  onClick,
}: IProps) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isAttentionModalVisible, setAttentionIsModalVisible] = useState(false)
  const { isSm, isXs } = useBreakpoint()
  const isMobile = isXs || isSm

  const getTotalSelectedCount = (): number => {
    if (transferMode === TRANSFER_MODES.ENTITIES) {
      return (
        objectsTargetKeys.length +
        usersTargetKeys.length +
        geozonesTargetKeys.length +
        driversTargetKeys.length +
        retranslationsTargetKeys.length
      )
    } else {
      return accountsTargetKeys.length
    }
  }

  const hasSelectedItems = () => {
    return getTotalSelectedCount() > 0
  }

  const isButtonDisabled = disabled || !hasSelectedItems()

  const tariffLimits = {
    limit: 50000,
    used: 400,
    tryingToAdd: getTotalSelectedCount(),
  }

  const limitObjectWord = declineObjectWord(tariffLimits.limit)

  const isLimitExceeded = () => {
    return tariffLimits.used + tariffLimits.tryingToAdd > tariffLimits.limit
  }

  const handleStartTransferClick = () => {
    if (isLimitExceeded()) {
      setIsModalVisible(true)
    } else {
      onClick?.()
    }
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
  }

  const handleCloseAttentionModal = () => {
    setAttentionIsModalVisible(false)
  }

  useBodyScrollLock(isModalVisible)

  return (
    <>
      <Flex
        vertical={isMobile}
        align={isMobile ? 'end' : 'inherit'}
        justify="flex-end"
        gap={!isMobile ? 32 : 24}
      >
        <PrimaryButton
          loading={loading}
          onClick={handleStartTransferClick}
          disabled={isButtonDisabled}
        >
          {text}
        </PrimaryButton>
      </Flex>

      <AttentionModal
        title="Внимание!"
        open={isAttentionModalVisible}
        onCancel={handleCloseAttentionModal}
        footer={null}
        width={450}
        centered
        closeIcon={<Icon name="closed_modal" size={16} />}
      >
        <Flex vertical gap={84}>
          <Text>
            Вы можете начать импорт данных клиента заново или продолжить с места
            остановки если процесс был прерван. Если вы решили повторить импорт
            не забудьте удалить загруженные объекты из системы-приемника.
          </Text>
          <Flex justify="flex-end" gap={16}>
            <DarkButton size="small" onClick={handleCloseAttentionModal}>
              Отмена
            </DarkButton>
            <PrimaryButton size="small">Начать заново</PrimaryButton>
          </Flex>
        </Flex>
      </AttentionModal>
      <LimitModal
        title="Внимание!"
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={450}
        centered
        closeIcon={<Icon name="closed_modal" size={16} />}
      >
        <Flex vertical gap={16}>
          <ErrorAlert
            title={
              <>
                <strong>Внимание!</strong> Превышен лимит по тарифу.
              </>
            }
            showIcon
          >
            <>
              <Text>
                Импорт невозможен: лимит {tariffLimits.limit} {limitObjectWord},
                используется {tariffLimits.used}, добавление ещё{' '}
                {tariffLimits.tryingToAdd} недоступно.
              </Text>
              <br />
              <Text>
                Обратитесь к менеджеру для расширения тарифного плана.
              </Text>
            </>
          </ErrorAlert>
          <Flex justify="flex-end">
            <DarkButton size="small" onClick={handleCloseModal}>
              Отмена
            </DarkButton>
          </Flex>
        </Flex>
      </LimitModal>
    </>
  )
}
